import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme
} from '@mui/material';
import { useThemeMode } from '../../common/contexts/ThemeContext';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../../common/hooks/useAuth';
import ResetPasswordModal from '../ResetPasswordModal/ResetPasswordModal';

/**
 * LoginModal Component
 * 
 * A modal dialog for user authentication that appears over the homepage.
 * Styled consistently with the dark theme and gradient accents.
 * 
 * Features:
 * - Email and password authentication
 * - Password visibility toggle
 * - Form validation and error handling
 * - Loading states during authentication
 * - Dark theme styling consistent with the app
 */
interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ open, onClose, onLoginSuccess }) => {
  const { login, error: authError, clearError } = useAuth();
  const theme = useTheme();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [touched, setTouched] = useState({
    email: false,
    password: false
  });

  const gradientText = {
    background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
  } as const;

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear errors when user starts typing
    if (error) {
      setError('');
    }
    if (authError) {
      clearError();
    }
  };

  const handleBlur = (field: string) => () => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const validateForm = () => {
    const errors: string[] = [];
    
    if (!formData.email) {
      errors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    if (!formData.password) {
      errors.push('Password is required');
    } else if (formData.password.length < 6) {
      errors.push('Password must be at least 6 characters');
    }
    
    return errors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const success = await login(formData);
      
      if (success) {
        // Reset form
        setFormData({ email: '', password: '' });
        setTouched({ email: false, password: false });
        onClose();
        onLoginSuccess(); // Navigate to dashboard
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({ email: '', password: '' });
      setTouched({ email: false, password: false });
      setError('');
      clearError();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          border: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
          borderRadius: 3,
          boxShadow: isDark ? '0 12px 48px rgba(0,0,0,0.8)' : '0 12px 48px rgba(0,0,0,0.3)'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: isDark 
              ? 'linear-gradient(180deg, rgba(11,11,11,0.95), rgba(0,0,0,0.95))'
              : 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(245,245,247,0.95))'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LoginIcon sx={{ color: '#000', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  ...gradientText,
                  fontWeight: 700,
                  mb: 0.5
                }}
              >
                Welcome Back
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Sign in to access your dashboard
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: '#ff1744',
                backgroundColor: 'rgba(255,23,68,0.15)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Error Alert */}
        {(error || authError) && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert
              severity="error"
              sx={{
                backgroundColor: 'rgba(255,23,68,0.15)',
                border: '1px solid rgba(255,23,68,0.3)',
                color: '#ff1744',
                '& .MuiAlert-icon': {
                  color: '#ff1744'
                }
              }}
            >
              {error || authError}
            </Alert>
          </Box>
        )}

        {/* Login Form */}
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            onBlur={handleBlur('email')}
            error={touched.email && !formData.email}
            helperText={touched.email && !formData.email ? 'Email is required' : ''}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDark ? 'rgba(11,11,11,0.8)' : 'rgba(255,255,255,0.8)',
                color: theme.palette.text.primary,
                border: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
                '&:hover': {
                  borderColor: isDark ? 'rgba(0,180,255,0.5)' : 'rgba(0,180,255,0.4)'
                },
                '&.Mui-focused': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: isDark ? '0 0 8px rgba(0,180,255,0.3)' : '0 0 8px rgba(0,180,255,0.2)'
                },
                '& fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
                '&.Mui-focused': {
                  color: theme.palette.primary.main
                }
              },
              '& .MuiFormHelperText-root': {
                color: theme.palette.text.secondary
              }
            }}
            disabled={isLoading}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange('password')}
            onBlur={handleBlur('password')}
            error={touched.password && !formData.password}
            helperText={touched.password && !formData.password ? 'Password is required' : ''}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    disabled={isLoading}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDark ? 'rgba(11,11,11,0.8)' : 'rgba(255,255,255,0.8)',
                color: theme.palette.text.primary,
                border: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
                '&:hover': {
                  borderColor: isDark ? 'rgba(0,180,255,0.5)' : 'rgba(0,180,255,0.4)'
                },
                '&.Mui-focused': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: isDark ? '0 0 8px rgba(0,180,255,0.3)' : '0 0 8px rgba(0,180,255,0.2)'
                },
                '& fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
                '&.Mui-focused': {
                  color: theme.palette.primary.main
                }
              },
              '& .MuiFormHelperText-root': {
                color: theme.palette.text.secondary
              }
            }}
            disabled={isLoading}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} sx={{ color: theme.palette.primary.main }} /> : <LoginIcon />}
            sx={{
              py: 1.5,
              mb: 3,
              borderRadius: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
              color: '#000',
              '&:hover': {
                background: 'linear-gradient(90deg, #9A3BF2, #10C4FF)',
                boxShadow: '0 8px 24px rgba(0,180,255,0.35)',
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: 'rgba(138,43,226,0.3)',
                color: 'rgba(255,255,255,0.3)'
              },
              transition: 'all 0.25s ease'
            }}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>

          {/* Additional Links */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="body2"
              component="button"
              type="button"
              onClick={() => setShowResetModal(true)}
              sx={{
                color: theme.palette.primary.main,
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                font: 'inherit',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Forgot your password?
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 2 }}>
              Don't have an account?{' '}
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: '#00B4FF',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Contact your administrator
              </Typography>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      {/* Reset Password Modal */}
      <ResetPasswordModal
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        mode="request"
      />
    </Dialog>
  );
};

export default LoginModal;

